import {useApiResource} from '~/composables/api/useApiResource'
import {readonly, ref} from 'vue'

export const useAuthApi = () => {
    const authStore = useAuthStore()

    const loading = ref(false)
    const error = ref(null)

    const login = async (email, password) => {
        loading.value = true
        error.value = null
        try {
            // Usar el método login del store que incluye fetchUser
            return await authStore.login(email, password)
        } catch (e) {
            error.value = e
            throw e
        } finally {
            loading.value = false
        }
    }

    const refresh = async () => {
        loading.value = true
        error.value = null
        try {
            // Usar el método refreshAccessToken del store que incluye fetchUser
            return await authStore.refreshAccessToken()
        } catch (e) {
            error.value = e
            throw e
        } finally {
            loading.value = false
        }
    }

    const logout = async () => {
        loading.value = true
        error.value = null
        try {
            // Llamar al endpoint de logout en el backend
            if (authStore.accessToken) {
                const config = useRuntimeConfig()
                await $fetch('/auth/logout', {
                    method: 'POST',
                    baseURL: config.public.apiBase,
                    headers: {
                        Authorization: `Bearer ${authStore.accessToken}`
                    }
                }).catch(() => {
                    // Ignorar errores del endpoint, continuar con limpieza local
                })
            }

            // Limpiar datos locales
            authStore.clearAuth()

            if (import.meta.client) {
                await navigateTo('/login')
            }
        } catch (e) {
            error.value = e
            throw e
        } finally {
            loading.value = false
        }
    }

    // Use useApiResource for password-related endpoints
    const { loading: passwordLoading, error: passwordError, customEndpoint } = useApiResource('auth', {
        deserialize: true,
        methods: { list: false, getById: false, create: false, update: false, delete: false }
    })

    const requestPassword = customEndpoint((email) => ({
        url: '/auth/request-password',
        method: 'POST',
        data: { email },
        _skipAuth: true,
        deserialize: true
    }))

    const resetPassword = customEndpoint((token, password) => ({
        url: '/auth/reset-password',
        method: 'POST',
        data: { token, password },
        _skipAuth: true,
        deserialize: true
    }))

    const updateProfile = customEndpoint((data) => ({
        url: '/auth/me',
        method: 'PATCH',
        data,
        deserialize: true
    }))

    const impersonate = customEndpoint((targetUserId, reason = '') => ({
        url: '/auth/impersonations',
        method: 'POST',
        data: { targetUserId, reason },
        deserialize: true
    }))

    const stopImpersonation = customEndpoint(() => ({
        url: '/auth/impersonations/logout',
        method: 'POST',
        deserialize: true
    }))

    return {
        loading: readonly(loading),
        error: readonly(error),
        passwordLoading,
        passwordError,
        login,
        refresh,
        logout,
        requestPassword,
        resetPassword,
        updateProfile,
        impersonate,
        stopImpersonation,
    }
}
